import { useState, useCallback } from "react"
import { isDiscovered, useCodexDiscovery } from "@/lib/codex-discovery"

export type CodexTab = "towers" | "enemies" | "items"

interface CodexState {
  isOpen: boolean
  activeTab: CodexTab
  selectedEntity: {
    towers: string | null
    enemies: string | null
    items: string | null
  }
}

export const useCodexState = () => {
  const [state, setState] = useState<CodexState>({
    isOpen: false,
    activeTab: "towers",
    selectedEntity: {
      towers: null,
      enemies: null,
      items: null,
    },
  })

  // Subscribing keeps the grids reactive: something discovered while the codex
  // is open stops being greyed out without needing a reopen.
  useCodexDiscovery()

  const openCodex = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: true }))
  }, [])

  const closeCodex = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }))
  }, [])

  const setActiveTab = useCallback((tab: CodexTab) => {
    setState((prev) => ({ ...prev, activeTab: tab }))
  }, [])

  const selectEntity = useCallback((entityId: string | null) => {
    setState((prev) => ({
      ...prev,
      selectedEntity: {
        ...prev.selectedEntity,
        [prev.activeTab]: entityId,
      },
    }))
  }, [])

  /**
   * An entry is readable once the player has encountered it - built the tower,
   * met the enemy, or held the item. Discovery is permanent and device-local
   * (see lib/codex-discovery.ts).
   */
  const isEntityUnlocked = useCallback(
    (entityId: string, tab: CodexTab): boolean => isDiscovered(tab, entityId),
    [],
  )

  return {
    isOpen: state.isOpen,
    activeTab: state.activeTab,
    selectedEntity: state.selectedEntity[state.activeTab],
    openCodex,
    closeCodex,
    setActiveTab,
    selectEntity,
    isEntityUnlocked,
  }
}
